import { logNotificationClick, logNotificationImpression } from 'api/braze'
import Image from 'components/Common/Image'
import LineBellIcon from 'LuxKit/Icons/line/LineBellIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItemThumbnail from 'LuxKit/List/ListItemThumbnail'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { dateDifference, useIntlDateFormatter } from 'lib/datetime/dateUtils'

const NewNotificationContainer = styled.div`
  width: ${rem(8)};
  height: ${rem(8)};
`

const NewDot = styled.div`
  width: ${rem(8)};
  height: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.highlight.primary.darkForeground};
`

interface Props {
  notification: App.UserNotification
}

function HeaderNotificationItem({ notification }: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const subtitle = useMemo(() => {
    if (!notification.created) return
    const difference = dateDifference(new Date(), notification.created)

    if (difference.days > 1) return dateFormatter(notification.created, INTL_DMY_CASUAL_FORMAT)
    if (difference.days === 1) {
      return intl.formatMessage({
        id: 'QeHYVJRKsjyciVFjaZ6m',
        defaultMessage: 'Yesterday',
        description: 'Time label for notification created yesterday in the site header',
      })
    }
    if (difference.hours >= 1) {
      return intl.formatMessage(
        {
          id: 'S7y3-vdRJOqRWBjTwU63',
          defaultMessage: '{count, plural, one {# hour ago} other {# hours ago}}',
          description: 'Time label for notification created hours ago in the site header',
        },
        { count: difference.hours },
      )
    }
    if (difference.minutes >= 1) {
      return intl.formatMessage(
        {
          id: 'DoAPgVrFZdg0fb28uo-W',
          defaultMessage: '{count, plural, one {# minute ago} other {# minutes ago}}',
          description: 'Time label for notification created minutes ago in the site header',
        },
        { count: difference.minutes },
      )
    }
    return intl.formatMessage({
      id: 'nf2YYLYBOg4D078DvJi5',
      defaultMessage: 'Less than a minute ago',
      description: 'Time label for notification created less than a minute ago',
    })
  }, [dateFormatter, notification, intl])

  const onClick = useCallback(() => {
    logNotificationClick(notification)
  }, [notification])

  useEffect(() => {
    logNotificationImpression(notification)
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ClickableListItem
      size="large"
      onClick={onClick}
      title={<><strong>{notification.title}</strong> {notification.description}</>}
      titleLineClamp={0}
      subtitle={subtitle}
      href={notification.url ?? undefined}
      target="_blank"
      startIcon={
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <NewNotificationContainer>{!notification.viewed && <NewDot />}</NewNotificationContainer>
          {notification.imageUrl && <Image width={48} height={48} image={{ url: notification.imageUrl }} />}
          {!notification.imageUrl && <ListItemThumbnail kind="neutral"><LineBellIcon /></ListItemThumbnail>}
        </Group>
      }
    />
  )
}

export default HeaderNotificationItem
